import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';

const TokenFeeInfoModal = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    aria-labelledby="token-fee-info-title"
  >
    <DialogTitle id="token-fee-info-title">How second-hand TND holds work</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <InfoOutlinedIcon color="primary" sx={{ mt: 0.5 }} />
          <Typography variant="body1">
            Every second-hand listing and offer temporarily locks <strong>1 TND hold</strong>. Holds are only captured when a deal is finalized and otherwise return automatically to the wallet balance.
          </Typography>
        </Stack>

        <Box>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            For sellers
          </Typography>
          <List dense>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <AutorenewIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="1 TND is held when you submit a listing request."
                secondary="It stays locked until the request is approved and the room reaches a final decision."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleOutlineIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="If a buyer and seller confirm the deal, the held TND is deducted."
                secondary="This confirms the transaction and pays the listing fee."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <AutorenewIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary="If the listing is withdrawn, rejected, or expires without a match, the TND hold is refunded."
                secondary="Refunds appear instantly in your wallet history."
              />
            </ListItem>
          </List>
        </Box>

        <Divider flexItem />

        <Box>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            For buyers
          </Typography>
          <List dense>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <AutorenewIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="1 TND is held each time you place an offer."
                secondary="The hold ensures that only serious offers reach the seller."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleOutlineIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="When both sides accept, the held TND is deducted to finalize the offer."
                secondary="The deduction is recorded as part of the finalized transaction."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <AutorenewIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary="If the seller rejects, you withdraw, or the offer expires, the TND hold is refunded."
                secondary="The refund is automatic and visible in your wallet statement."
              />
            </ListItem>
          </List>
        </Box>

        <Typography variant="caption" color="text.secondary">
          Need more details? Check the wallet history for timestamps and references of each hold, capture, and refund.
        </Typography>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="contained" color="primary">
        Got it
      </Button>
    </DialogActions>
  </Dialog>
);

TokenFeeInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

TokenFeeInfoModal.defaultProps = {
  onClose: () => {},
};

export default TokenFeeInfoModal;
